import { useState, useRef, useCallback, useEffect } from "react";
import styles from "./Funnel.module.css";
import ProgressBar from "./ProgressBar";
import EllipsisLoader from "../EllipsisLoader/EllipsisLoader";

import StepProduits from "./steps/StepProduits";
import StepEcheance from "./steps/StepEcheance";
import StepTravaux from "./steps/StepTravaux";
import StepHabitat from "./steps/StepHabitat";
import StepMessage from "./steps/StepMessage";
import StepLocalisation from "./steps/StepLocalisation";
import StepMagasin from "./steps/StepMagasin";
import StepInfos from "./steps/StepInfos";
import StepSavMessage from "./steps/StepSavMessage";

import { submitLead } from "@/lib/submitLead";
import { pushFormStart, pushFormStep, pushFormSubmit } from "@/lib/tracking";
import { resetTurnstile } from "@/components/Turnstile/TurnstileWidget";
import { resolveProducts } from "@/lib/productVariants";
import { getLocalisationMode } from "@/lib/localisationMode";
import { validateCommune } from "@/lib/formValidation";

export interface FunnelFormData {
  demande: string;
  produits: string[];
  echeance: string;
  travaux: string;
  habitat: string;
  message: string;
  savMessage: string;
  codePostal: string;
  ville: string;
  codeInsee: string;
  magasinId: string;
  magasinLabel: string;
  region: string;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  contactPreference: string[];
  optin: boolean;
  marketingOptin: boolean;
}

const INITIAL_DATA: FunnelFormData = {
  demande: "Devis",
  produits: [],
  echeance: "",
  travaux: "",
  habitat: "",
  message: "",
  savMessage: "",
  codePostal: "",
  ville: "",
  codeInsee: "",
  magasinId: "",
  magasinLabel: "",
  region: "",
  nom: "",
  prenom: "",
  email: "",
  telephone: "",
  contactPreference: [],
  optin: false,
  marketingOptin: false,
};

type Path = "Devis" | "SAV";
type Status = "form" | "success" | "error";

// Devis steps: Produits(1), Echeance(2), Travaux(3), Habitat(4), Message(5), Localisation(6), Infos(7)
// SAV steps:   SavMessage(1), Localisation(2), Infos(3)
// L'ancienne étape Demande(0) a été retirée : le funnel démarre sur Devis et le
// parcours SAV s'ouvre via un lien. La numérotation est conservée pour ne pas
// casser l'historique des step_index dans GA4.
const FIRST_STEP = 1;

const DEVIS_PROGRESS = [0, 14, 28, 42, 57, 71, 85, 100];
const SAV_PROGRESS = [0, 33, 66, 100];

/**
 * Mappe une step (index + path) au nom et à la valeur saisie qu'on pousse
 * dans le dataLayer via `form_step`. Les steps de submit final (infos)
 * retournent null car déjà couvertes par `form_submit`.
 *
 * Pour les champs message libres, on pousse un boolean filled/empty plutôt
 * que le contenu (PII + verbose). Le contenu reste dispo via form_submit.
 */
function getStepInfo(
  stepIndex: number,
  path: Path,
  data: FunnelFormData,
  mode: "cp" | "magasin",
): { name: string; value: unknown } | null {
  if (stepIndex === 0) return { name: "demande", value: data.demande };
  if (path === "Devis") {
    switch (stepIndex) {
      case 1: return { name: "produits", value: data.produits };
      case 2: return { name: "echeance", value: data.echeance };
      case 3: return { name: "travaux", value: data.travaux };
      case 4: return { name: "habitat", value: data.habitat };
      case 5: return { name: "message", value: data.message.trim().length > 0 ? "filled" : "empty" };
      case 6: return {
        name: "localisation",
        value: mode === "magasin"
          ? { region: data.region, magasin_id: data.magasinId, magasin_label: data.magasinLabel }
          : { code_postal: data.codePostal, code_insee: data.codeInsee, ville: data.ville },
      };
      default: return null;
    }
  }
  if (path === "SAV") {
    switch (stepIndex) {
      case 1: return { name: "sav_message", value: data.savMessage.trim().length > 0 ? "filled" : "empty" };
      case 2: return {
        name: "localisation",
        value: mode === "magasin"
          ? { region: data.region, magasin_id: data.magasinId, magasin_label: data.magasinLabel }
          : { code_postal: data.codePostal, code_insee: data.codeInsee, ville: data.ville },
      };
      default: return null;
    }
  }
  return null;
}

interface FunnelProps {
  onStatusChange?: (status: Status) => void;
  produits?: string;
  variant?: string;
  localisation?: string;
}

export default function Funnel({ onStatusChange, produits, variant, localisation }: FunnelProps) {
  const products = resolveProducts({ produits, variant });
  const mode = getLocalisationMode(localisation);
  const [currentStep, setCurrentStep] = useState(FIRST_STEP);
  const [formData, setFormData] = useState<FunnelFormData>(INITIAL_DATA);
  const [path, setPath] = useState<Path>("Devis");
  const [status, setStatus] = useState<Status>("form");
  const [, setDirection] = useState<1 | -1>(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [containerHeight, setContainerHeight] = useState<number | undefined>(
    undefined
  );
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Nombre de communes proposées pour le code postal courant. Sert uniquement à
  // savoir si le choix de commune est exigible (cf. validateCommune).
  const [communesCount, setCommunesCount] = useState<number | null>(0);

  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as
    | string
    | undefined;

  const stepRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!stepRef.current) return;
    // ResizeObserver : recalcule la hauteur quand le contenu interne change
    // (dropdown communes, message d'erreur asynchrone, widget Turnstile qui
    // se rend après chargement du script). Sans ça le container reste à la
    // hauteur initiale et coupe le contenu ajouté après le premier render.
    const el = stepRef.current;
    setContainerHeight(el.scrollHeight);
    const ro = new ResizeObserver(() => {
      setContainerHeight(el.scrollHeight);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [currentStep, path]);

  useEffect(() => {
    return () => {
      if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    };
  }, []);

  const updateStatus = useCallback(
    (s: Status) => {
      setStatus(s);
      onStatusChange?.(s);
    },
    [onStatusChange]
  );

  // Tracking refs : `formStartedRef` garantit que `form_start` n'est poussé
  // qu'une seule fois par session. `stateRef` reflète le dernier état React
  // pour pouvoir lire les valeurs fraîches depuis le setTimeout d'auto-advance
  // sans souci de stale closure.
  const formStartedRef = useRef(false);
  const stateRef = useRef({ currentStep, path, formData });
  stateRef.current = { currentStep, path, formData };

  const trackFormStart = useCallback(() => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    pushFormStart();
  }, []);

  const trackStepCompleted = useCallback((stepIndex: number) => {
    const { path: p, formData: d } = stateRef.current;
    const info = getStepInfo(stepIndex, p, d, mode);
    if (!info) return;
    pushFormStep({
      stepIndex,
      stepName: info.name,
      stepValue: info.value,
      formType: p === "SAV" ? "sav" : "devis",
    });
  }, []);

  const maxStep = path === "Devis" ? 7 : 3;
  const isLastStep = currentStep === maxStep;
  const progressValues =
    path === "SAV" ? SAV_PROGRESS : DEVIS_PROGRESS;
  const progress = progressValues[currentStep] ?? 0;

  const handleFieldChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleRadioAutoAdvance = (name: string, value: string) => {
    handleFieldChange(name, value);
    scheduleAutoAdvance();
  };

  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleAutoAdvance = useCallback(() => {
    if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    autoAdvanceTimer.current = setTimeout(() => {
      trackStepCompleted(stateRef.current.currentStep);
      setDirection(1);
      setCurrentStep((prev) => prev + 1);
    }, 350);
  }, [trackStepCompleted]);

  const switchPath = (next: Path) => {
    setErrors({});
    setFormData((prev) => ({ ...prev, demande: next }));
    setPath(next);
    setCurrentStep(FIRST_STEP);
  };

  // Le choix SAV reste mesuré comme avant (form_step "demande") pour suivre la
  // part de visiteurs qui l'empruntent.
  const openSav = () => {
    trackFormStart();
    pushFormStep({ stepIndex: 0, stepName: "demande", stepValue: "SAV", formType: "sav" });
    setDirection(1);
    switchPath("SAV");
  };

  const handleProduitsChange = (value: string[]) => {
    trackFormStart();
    setFormData((prev) => ({ ...prev, produits: value }));
  };

  const handleContactPrefChange = (selected: string[]) => {
    setFormData((prev) => ({ ...prev, contactPreference: selected }));
  };

  const handleOptinChange = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, optin: checked }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.optin;
      return next;
    });
  };

  const handleMarketingOptinChange = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, marketingOptin: checked }));
  };

  const handleCommuneSelect = (codeInsee: string, nom: string) => {
    setFormData((prev) => ({ ...prev, codeInsee, ville: nom }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.ville;
      delete next.codeInsee;
      return next;
    });
  };

  const handleRegionChange = (region: string) => {
    // Changer de région invalide le magasin précédemment choisi.
    setFormData((prev) => ({ ...prev, region, magasinId: "", magasinLabel: "" }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.region;
      delete next.magasin;
      return next;
    });
  };

  const handleMagasinSelect = (magasinId: string, label: string, region: string) => {
    setFormData((prev) => ({ ...prev, magasinId, magasinLabel: label, region }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.magasin;
      return next;
    });
  };

  const validateStep = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (path === "Devis") {
      if (currentStep === 1 && formData.produits.length === 0) {
        newErrors.produits = "Veuillez sélectionner au moins un produit";
      }
      if (currentStep === 2 && !formData.echeance) {
        newErrors.echeance = "Veuillez sélectionner une échéance";
      }
      if (currentStep === 3 && !formData.travaux) {
        newErrors.travaux = "Veuillez sélectionner un type de travaux";
      }
      if (currentStep === 4 && !formData.habitat) {
        newErrors.habitat = "Veuillez sélectionner un type d'habitat";
      }
      // Step 5 (message) is optional
      if (currentStep === 6) {
        if (mode === "magasin") {
          if (!formData.region) newErrors.region = "Veuillez sélectionner votre région";
          if (!formData.magasinId) newErrors.magasin = "Veuillez sélectionner votre magasin";
        } else {
          if (!formData.codePostal || !/^\d{5}$/.test(formData.codePostal)) {
            newErrors.codePostal = "Code postal invalide (5 chiffres)";
          }
          if (!formData.codeInsee) {
            newErrors.ville = "Veuillez sélectionner votre commune dans la liste";
          }
        }
      }
      if (currentStep === 7) {
        validateInfos(newErrors);
      }
    }

    if (path === "SAV") {
      // Step 1 (savMessage) is optional
      if (currentStep === 2) {
        if (mode === "magasin") {
          if (!formData.region) newErrors.region = "Veuillez sélectionner votre région";
          if (!formData.magasinId) newErrors.magasin = "Veuillez sélectionner votre magasin";
        } else {
          if (!formData.codePostal || !/^\d{5}$/.test(formData.codePostal)) {
            newErrors.codePostal = "Code postal invalide (5 chiffres)";
          }
          if (!formData.codeInsee) {
            newErrors.ville = "Veuillez sélectionner votre commune dans la liste";
          }
        }
      }
      if (currentStep === 3) {
        validateInfos(newErrors);
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateInfos = (newErrors: Record<string, string>) => {
    if (!formData.nom.trim()) {
      newErrors.nom = "Le nom est requis";
    }
    if (!formData.prenom.trim()) {
      newErrors.prenom = "Le prénom est requis";
    }
    if (
      !formData.email.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      newErrors.email = "Email invalide";
    }
    if (
      !formData.telephone.trim() ||
      !/^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/.test(
        formData.telephone
      )
    ) {
      newErrors.telephone = "Numéro de téléphone invalide";
    }
    if (!formData.optin) {
      newErrors.optin = "Vous devez accepter les conditions";
    }
    if (!turnstileToken) {
      newErrors.turnstile = "Merci de valider le contrôle anti-robot";
    }
    if (mode === "magasin") {
      if (!formData.codePostal || !/^\d{5}$/.test(formData.codePostal)) {
        newErrors.codePostal = "Code postal invalide (5 chiffres)";
      } else if (!validateCommune(formData.ville, communesCount)) {
        newErrors.ville = "Veuillez sélectionner votre commune dans la liste";
      }
    }
  };

  const goNext = () => {
    if (!validateStep()) return;
    trackFormStart();
    trackStepCompleted(currentStep);
    setDirection(1);
    setCurrentStep((prev) => Math.min(prev + 1, maxStep));
  };

  const goBack = () => {
    setDirection(-1);
    if (path === "SAV" && currentStep === FIRST_STEP) {
      switchPath("Devis");
      return;
    }
    setErrors({});
    setCurrentStep((prev) => Math.max(prev - 1, FIRST_STEP));
  };

  const isFirstScreen = path === "Devis" && currentStep === FIRST_STEP;

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const result = await submitLead(formData, turnstileToken, mode);
      if (result.ok) {
        pushFormSubmit(formData, result);
        updateStatus("success");
        return;
      }
      // Erreurs fatales (pas de réessai possible) : on bascule en status error
      // pour que le parent affiche un écran d'erreur global.
      if (
        result.error === "server_misconfigured" ||
        result.error === "lead_insert_failed"
      ) {
        updateStatus("error");
        return;
      }
      // Erreurs réessayables (captcha, CP inconnu, email mal tapé…) : on reste
      // sur le form et on affiche le message inline. Reset Turnstile au cas où.
      setSubmitError(result.message);
      setTurnstileToken("");
      resetTurnstile();
    } catch {
      updateStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const infosProps = {
    nom: formData.nom,
    prenom: formData.prenom,
    email: formData.email,
    telephone: formData.telephone,
    codePostal: formData.codePostal,
    askCodePostal: mode === "magasin",
    ville: formData.ville,
    onCommuneChange: (codePostal: string, ville: string) => {
      setFormData((prev) => ({ ...prev, codePostal, ville }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next.codePostal;
        delete next.ville;
        return next;
      });
    },
    onCommunesLoaded: setCommunesCount,
    contactPreference: formData.contactPreference,
    optin: formData.optin,
    marketingOptin: formData.marketingOptin,
    onChange: handleFieldChange,
    onContactPrefChange: handleContactPrefChange,
    onOptinChange: handleOptinChange,
    onMarketingOptinChange: handleMarketingOptinChange,
    errors,
    turnstileSiteKey: turnstileSiteKey,
    turnstileToken,
    onTurnstileToken: (token: string) => {
      setTurnstileToken(token);
      setErrors((prev) => {
        const next = { ...prev };
        delete next.turnstile;
        return next;
      });
    },
    onTurnstileExpired: () => setTurnstileToken(""),
  };

  const renderStep = () => {
    if (path === "Devis") {
      switch (currentStep) {
        case 1:
          return (
            <StepProduits
              value={formData.produits}
              products={products}
              onChange={handleProduitsChange}
            />
          );
        case 2:
          return (
            <StepEcheance
              value={formData.echeance}
              onChange={(v) => handleRadioAutoAdvance("echeance", v)}
            />
          );
        case 3:
          return (
            <StepTravaux
              value={formData.travaux}
              onChange={(v) => handleRadioAutoAdvance("travaux", v)}
            />
          );
        case 4:
          return (
            <StepHabitat
              value={formData.habitat}
              onChange={(v) => handleRadioAutoAdvance("habitat", v)}
            />
          );
        case 5:
          return (
            <StepMessage
              value={formData.message}
              onChange={handleFieldChange}
            />
          );
        case 6:
          return mode === "magasin" ? (
            <StepMagasin
              region={formData.region}
              magasinId={formData.magasinId}
              onSelect={handleMagasinSelect}
              onRegionChange={handleRegionChange}
              errors={errors}
            />
          ) : (
            <StepLocalisation
              codePostal={formData.codePostal}
              ville={formData.ville}
              codeInsee={formData.codeInsee}
              onChange={handleFieldChange}
              onCommuneSelect={handleCommuneSelect}
              errors={errors}
            />
          );
        case 7:
          return <StepInfos {...infosProps} />;
        default:
          return null;
      }
    }

    if (path === "SAV") {
      switch (currentStep) {
        case 1:
          return (
            <StepSavMessage
              value={formData.savMessage}
              onChange={handleFieldChange}
            />
          );
        case 2:
          return mode === "magasin" ? (
            <StepMagasin
              region={formData.region}
              magasinId={formData.magasinId}
              onSelect={handleMagasinSelect}
              onRegionChange={handleRegionChange}
              errors={errors}
            />
          ) : (
            <StepLocalisation
              codePostal={formData.codePostal}
              ville={formData.ville}
              codeInsee={formData.codeInsee}
              onChange={handleFieldChange}
              onCommuneSelect={handleCommuneSelect}
              errors={errors}
            />
          );
        case 3:
          return <StepInfos {...infosProps} />;
        default:
          return null;
      }
    }

    return null;
  };

  if (status !== "form") return null;

  return (
    <div className={styles.funnel}>
      <ProgressBar percent={progress} />
      <p className={styles.title}>
        {path === "SAV" ? "Suivre un projet en cours" : "Votre demande de devis"}
      </p>

      <div
        className={styles.stepContainer}
        style={{
          height: containerHeight !== undefined ? containerHeight : "auto",
        }}
      >
        <div
          ref={stepRef}
          className={styles.stepContent}
          style={{
            transform: `translateX(0)`,
          }}
          key={currentStep}
        >
          {renderStep()}
        </div>
      </div>

      {isFirstScreen && (
        <p className={styles.savLink}>
          Vous avez déjà un projet en cours ?{" "}
          <button type="button" onClick={openSav}>
            Suivre mon projet
          </button>
        </p>
      )}

      {submitError && (
        <p
          role="alert"
          style={{
            marginTop: 12,
            padding: "8px 12px",
            fontSize: 14,
            background: "var(--color-error-bg, #f2dede)",
            color: "var(--color-error-text, #a94442)",
            textAlign: "center",
          }}
        >
          {submitError}
        </p>
      )}

      <div className={styles.navigation}>
        <span
          className={`${styles.backButton} ${
            isFirstScreen ? styles.hidden : ""
          }`}
          onClick={goBack}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && goBack()}
        >
          RETOUR
        </span>

        {isLastStep ? (
          <button
            type="button"
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? <EllipsisLoader /> : "Envoyer ma demande"}
          </button>
        ) : (
          <button type="button" className={styles.nextButton} onClick={goNext}>
            SUIVANT
          </button>
        )}
      </div>

      <p className={styles.mentions}>
        Vos données sont protégées et ne seront jamais partagées.
      </p>
    </div>
  );
}
